/**
 * Commands the page palette can run.
 *
 * A merchant row keeps a fixed score so it stays under every page. An action
 * does the same, in the band between those two. The words are the ones a
 * reader types for the command, scored the way a page is scored.
 */
import { pageSearchScore } from './page-search.ts'
import { nextPreference, type Theme } from './theme.ts'

/** The commands, in the order the palette shows when their scores tie. */
export const PALETTE_ACTION_IDS = [
  'star',
  'alert',
  'time-zone',
  'theme',
  'customize',
  'sign-out'
] as const

/** One command the palette can run. */
export type PaletteActionId = (typeof PALETTE_ACTION_IDS)[number]

/**
 * Words besides the label. The label is the name. These are the other words
 * a reader types for that command.
 */
const ACTION_WORDS: Record<PaletteActionId, readonly string[]> = {
  star: [],
  alert: [],
  'time-zone': ['timezone'],
  theme: ['dark', 'light', 'mode'],
  customize: [],
  'sign-out': ['log out', 'logout']
}

const ACTION_RANK: Record<PaletteActionId, number> = {
  star: 0,
  alert: 1,
  'time-zone': 2,
  theme: 3,
  customize: 4,
  'sign-out': 5
}

/** The display data for one command the palette shows. */
export interface PaletteAction {
  id: PaletteActionId
  /** The words the row shows. */
  label: string
}

/**
 * The search fields for one action: the label, then its extra words.
 *
 * @param label - The words the row shows.
 * @param id - Which command this is. That chooses the extra words.
 * @returns The fields `pageSearchScore` ranks.
 */
export function actionSearchValue(label: string, id: PaletteActionId): string {
  return [label, ACTION_WORDS[id].join(' ')]
    .map(field => field.replace(/\s+/g, ' '))
    .join('\n')
}

/**
 * The cmdk value for one action.
 *
 * A page value is six fields. A merchant value is three fields that start
 * with `merchant`. Two fields that start with `action` are neither, so the
 * palette filter can keep the row without ranking it as a page.
 *
 * @param id - Which command this is.
 * @returns The value cmdk should filter.
 */
export function actionCommandValue(id: PaletteActionId): string {
  return `action\n${id}`
}

/**
 * Whether cmdk should keep this row as an action.
 *
 * @param value - The item value cmdk is scoring.
 * @returns True when `actionCommandValue` built it.
 */
export function isActionCommandValue(value: string): boolean {
  const parts = value.split('\n')
  const id = parts[1] ?? ''

  return (
    parts.length === 2 &&
    parts[0] === 'action' &&
    (PALETTE_ACTION_IDS as readonly string[]).includes(id)
  )
}

/**
 * The actions the query matches, best match first.
 *
 * An empty query matches nothing. `pageSearchScore` scores an empty query as
 * 1, which would offer every action before the reader has typed. Ties keep
 * the command order.
 *
 * @param actions - The commands this reader may run.
 * @param search - What the reader typed.
 * @returns The matching commands.
 */
export function matchingPaletteActions<
  T extends { id: PaletteActionId; label: string }
>(actions: readonly T[], search: string): T[] {
  if (search.trim() === '') return []

  return (
    actions
      .map(action => ({
        action,
        score: pageSearchScore(
          actionSearchValue(action.label, action.id),
          search
        )
      }))
      .filter(item => item.score > 0)
      // `toSorted` is ES2023; this app's TypeScript lib is ES2022. `filter`
      // returned a fresh array, so the sort mutates nothing the caller holds.
      // oxlint-disable-next-line unicorn/no-array-sort
      .sort(
        (a, b) =>
          b.score - a.score ||
          ACTION_RANK[a.action.id] - ACTION_RANK[b.action.id]
      )
      .map(item => item.action)
  )
}

/**
 * The commands this reader may run, in palette order.
 *
 * The star and the alert are omitted when that control is not on the page.
 * The other four are always offered. The theme label names the theme a run
 * switches to, the same words the theme toggle uses.
 *
 * @param input - Which controls are on the page, and the current theme.
 * @returns The commands to offer.
 */
export function buildPaletteActions(input: {
  star: { starred: boolean } | null
  alert: boolean
  theme: Theme
}): PaletteAction[] {
  const actions: PaletteAction[] = []
  if (input.star) {
    actions.push({
      id: 'star',
      label: input.star.starred ? 'Unstar this page' : 'Star this page'
    })
  }
  if (input.alert) {
    actions.push({
      id: 'alert',
      label: 'Set an alert on this page'
    })
  }
  actions.push(
    {
      id: 'time-zone',
      label: 'Change the time zone'
    },
    {
      id: 'theme',
      label: `Switch to ${nextPreference(input.theme)} theme`
    },
    {
      id: 'customize',
      label: 'Customize the rail'
    },
    {
      id: 'sign-out',
      label: 'Sign out'
    }
  )

  return actions
}
